/**
 * Session Health Indicator Component
 * Shows current user's activity status (Active/Idle/Inactive)
 * Transparent monitoring - user always sees their own status
 * 
 * IMPORTANT: This component now tracks user activity via heartbeats
 * to ensure accurate status when user has an active attendance session.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { dedupedGet } from '../../lib/requestDedupe';
import { Activity, Clock, AlertCircle, ZapOff } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { usePoller } from '../../hooks/usePoller';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  active: {
    label: 'Active',
    color: 'bg-green-500',
    ringColor: 'ring-green-500/30',
    textColor: 'text-green-700',
    bgLight: 'bg-green-50',
    icon: Activity,
    description: 'You are actively working'
  },
  idle: {
    label: 'Idle',
    color: 'bg-yellow-500',
    ringColor: 'ring-yellow-500/30',
    textColor: 'text-yellow-700',
    bgLight: 'bg-yellow-50',
    icon: Clock,
    description: 'No recent activity detected'
  },
  inactive: {
    label: 'Inactive',
    color: 'bg-red-500',
    ringColor: 'ring-red-500/30',
    textColor: 'text-red-700',
    bgLight: 'bg-red-50',
    icon: AlertCircle,
    description: 'Extended period of inactivity'
  },
  on_break: {
    label: 'On Break',
    color: 'bg-orange-500',
    ringColor: 'ring-orange-500/30',
    textColor: 'text-orange-700',
    bgLight: 'bg-orange-50',
    icon: Clock,
    description: 'Currently on break'
  },
  it_issue: {
    label: 'Downtime',
    color: 'bg-amber-500',
    ringColor: 'ring-amber-500/30',
    textColor: 'text-amber-700',
    bgLight: 'bg-amber-50',
    icon: ZapOff,
    description: 'System downtime - work blocked'
  }
};

const SessionHealthIndicator = ({ compact = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const heartbeatIntervalRef = useRef(null);

  const fetchStatus = useCallback(async (signal) => {
    try {
      // IDLE-BADGE-STALE-FIX (Jun 2026) — explicit request timeout. Every
      // caller of this URL goes through dedupedGet, which joins an already
      // in-flight request and ignores the joiner's abort signal; a single
      // hung request therefore wedged the poller's overlap guard forever,
      // freezing the badge (e.g. stuck on "Idle" while the API said active).
      // With a hard 20s timeout the promise always settles, so polling
      // self-recovers after any hang.
      const response = await dedupedGet(`${API}/api/session-health/my-status`, { signal, timeout: 20000 });
      setStatus(response.data);
    } catch (err) {
      if (axios.isCancel(err)) {
        console.warn('Session status poll exceeded its 25s window and was cancelled — retrying with backoff');
        return false;
      }
      console.error('Error fetching session status:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Send heartbeat to keep session active.
  // IDLE-BADGE-SYNC (Jun 2026) — failed heartbeats used to be swallowed
  // silently with no retry; the next chance was 5 minutes later, past the
  // 7-min "active" threshold, producing false Idle badges. Now: warn-log
  // every failure and retry ONCE after 20s before falling back to the
  // regular 5-min interval.
  const heartbeatRetryRef = useRef(null);
  const sendHeartbeat = useCallback(async (isRetry = false) => {
    try {
      await axios.post(`${API}/api/session-health/heartbeat`);
      lastActivityRef.current = Date.now();
      if (heartbeatRetryRef.current) {
        clearTimeout(heartbeatRetryRef.current);
        heartbeatRetryRef.current = null;
      }
    } catch (err) {
      console.warn(
        `[SessionHealth] Heartbeat failed${isRetry ? ' (retry)' : ''}: ${err?.message || err}. ` +
        (isRetry
          ? 'Will try again on the regular 5-min interval.'
          : 'Retrying once in 20s to avoid a false Idle badge.')
      );
      if (!isRetry) {
        if (heartbeatRetryRef.current) clearTimeout(heartbeatRetryRef.current);
        heartbeatRetryRef.current = setTimeout(() => sendHeartbeat(true), 20000);
      }
    }
  }, []);

  // Track user activity
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    // Only send heartbeat if more than 2 minutes since last activity
    if (now - lastActivityRef.current > 120000) {
      sendHeartbeat();
    }
    lastActivityRef.current = now;
  }, [sendHeartbeat]);

  useEffect(() => {
    fetchStatus();
    
    // Send initial heartbeat on mount
    sendHeartbeat();
    
    // Send heartbeat every 5 minutes to maintain "active" status
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 300000);
    
    // Track user activity events
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });
    
    // Track page visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User returned to tab - send heartbeat.
        // (usePoller below already refetches status on tab return.)
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (heartbeatRetryRef.current) {
        clearTimeout(heartbeatRetryRef.current);
        heartbeatRetryRef.current = null;
      }
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchStatus, sendHeartbeat, handleUserActivity]);

  // POLL-HERD-001: staggered, overlap-safe, backoff-aware status polling.
  usePoller(fetchStatus, 30000, { immediate: false });

  if (loading || !status) {
    return (
      <div className="w-3 h-3 bg-gray-300 rounded-full animate-pulse" />
    );
  }

  // Avoid visual duplication — while a downtime is active the SystemDowntimeButton
  // in the header already surfaces a prominent "Downtime {timer}" chip. Rendering
  // our own "Downtime" pill here as well produced the duplicate chip users saw.
  if (status.status === 'it_issue') {
    return null;
  }

  const config = STATUS_CONFIG[status.status] || STATUS_CONFIG.inactive;
  const StatusIcon = config.icon;

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={`w-3 h-3 rounded-full ${config.color} ring-2 ${config.ringColor} cursor-help`}
              data-testid="session-health-indicator"
            />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <StatusIcon className={`w-4 h-4 ${config.textColor}`} />
                <span className="font-medium">{config.label}</span>
              </div>
              <p className="text-xs text-gray-500">{config.description}</p>
              {status.minutes_since_activity !== null && (
                <p className="text-xs text-gray-400">
                  Last activity: {Math.round(status.minutes_since_activity)} min ago
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`flex items-center gap-2 px-2 py-1 rounded-full ${config.bgLight} cursor-help`}
            data-testid="session-health-indicator"
          >
            <div className={`w-2 h-2 rounded-full ${config.color} animate-pulse`} />
            <span className={`text-xs font-medium ${config.textColor}`}>
              {config.label}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${config.textColor}`} />
              <span className="font-medium">{config.label}</span>
            </div>
            <p className="text-xs text-gray-500">{config.description}</p>
            {status.minutes_since_activity !== null && (
              <p className="text-xs text-gray-400">
                Last activity: {Math.round(status.minutes_since_activity)} min ago
              </p>
            )}
            <div className="pt-1 border-t border-gray-100 mt-1">
              <p className="text-xs text-gray-400">
                Active: 0-{status.thresholds?.active || 7} min | 
                Idle: {status.thresholds?.active || 7}-{status.thresholds?.idle || 20} min
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SessionHealthIndicator;
